import {Component} from 'panel';

import {eventWasOutside} from 'common/util/dom';
import {registerMPElement} from 'common/util/register-element';
import {MessagePartType} from './constants';

import template from './index.jade';
import css from './index.styl?inline';

interface GlobalActionBarState {
  shortCode: string;
  shortUrlLoading: boolean;
  shortUrlCopied: boolean;
  showBetaBanner: boolean;
}

export default class GlobalActionBar extends Component<GlobalActionBarState, null, GlobalActionBar> {
  private shortUrlCopiedTimeout: number;
  private shortUrlEl: HTMLInputElement;

  public static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'beta-banner-parts': {
        type: `json`,
        description: `interface MessagePart[]`,
      },
      'beta-banner-controls': {
        type: `json`,
      },
      'beta-banner-feedback-email': `string`,
      'beta-banner-feedback-email-subject': `string`,
      'beta-banner-feedback-label': {
        type: `string`,
        default: `Feedback`,
        description: `button label for the feedback button`,
      },
      hidden: `boolean`,
    };
  }

  get config() {
    return {
      defaultState: {
        shortCode: null,
        shortUrlLoading: false,
        shortUrlCopied: false,
        showBetaBanner: true,
      },
      css,
      template,
      useShadowDom: true,
      helpers: {
        MessagePartType,

        handleAction: (actionName, value) =>
          this.dispatchEvent(new CustomEvent(`action`, {detail: {value, actionName}})),

        getBetaBannerControls: () => {
          const controls = this.attr(`beta-banner-controls`) || [];
          const isEmpty = !controls.length && !this.attr(`beta-banner-feedback-email`);

          return isEmpty
            ? []
            : [
                ...controls,
                ...(this.attr(`beta-banner-feedback-email`)
                  ? [
                      {
                        icon: `email`,
                        value: `feedback`,
                        label: this.attr(`beta-banner-feedback-label`),
                      },
                    ]
                  : []),
                {
                  icon: `x`,
                  value: `dismiss`,
                },
              ];
        },

        betaBannerControlSelect: (ev) => {
          const {value} = ev.detail;

          if (value === `dismiss`) {
            this.update({showBetaBanner: false});
          } else if (value === `feedback`) {
            const subject = this.attr(`beta-banner-feedback-email-subject`);
            window.open(
              `mailto:${this.attr(`beta-banner-feedback-email`)}${subject ? `?subject=${subject}` : ``}`,
              `_blank`,
            );
          } else {
            this.dispatchEvent(new CustomEvent(`action`, {detail: {actionName: value, value}}));
          }
        },

        createShortUrl: async () => {
          if (!this.state.shortUrlLoading) {
            const targetUrl = window.location.toString();
            try {
              this.update({shortUrlLoading: true});
              const shortUrlFetch = await fetch(`/s/`, {
                credentials: `include`,
                method: `POST`,
                body: targetUrl,
              });
              const shortCodeResp = await shortUrlFetch.text();
              if (shortUrlFetch.status !== 200) {
                throw Error(shortCodeResp);
              }
              this.update({shortCode: shortCodeResp, shortUrlLoading: false});
              this.trackShortUrl(`Created`, {
                targetUrl,
                short_code: this.state.shortCode, // keep snake_case to match server-side events
              });
            } catch (err) {
              this.update({shortUrlLoading: false});
              this.trackShortUrl(`Create Failed`, {targetUrl, error: err});
              throw err;
            }
          }
        },

        getShortUrl: () => {
          return `${window.location.protocol}//${window.location.hostname}/s/${this.state.shortCode}`;
        },

        handleShortUrlInputInsert: (inputEl: HTMLInputElement) => {
          this.shortUrlEl = inputEl;
          this.helpers.copyShortUrl();
        },

        copyShortUrl: () => {
          this.shortUrlEl.select();
          this.shortUrlEl.style.width = `${this.shortUrlEl.scrollWidth}px`; // ensure link doesn't require text scrolling

          if (document.execCommand(`copy`)) {
            this.trackShortUrl(`Copied`, {short_code: this.state.shortCode});
            this.update({shortUrlCopied: true});
            this.shortUrlCopiedTimeout = window.setTimeout(this.helpers.clearShortUrlCopied, 2000);
          }
        },

        clearShortUrlCopied: () => {
          this.update({shortUrlCopied: false});
          window.clearTimeout(this.shortUrlCopiedTimeout);
        },

        closeShortUrl: () => {
          this.update({shortCode: null});
          this.trackShortUrl(`Closed`, {short_code: this.state.shortCode});
        },

        handleDocumentClick: (ev) => {
          if (eventWasOutside(ev, this)) {
            this.update({shortCode: null});
          }
        },

        handleDomContentLoaded: () => {
          // re-align self if mp-help-widget isn't present due to gdpr opt-out
          if (!document.querySelector(`mp-help-widget`)) {
            this.style.right = `20px`;
          }
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();
    document.addEventListener(`click`, this.helpers.handleDocumentClick);
    window.addEventListener(`DOMContentLoaded`, this.helpers.handleDomContentLoaded);
  }

  public disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(`click`, this.helpers.handleDocumentClick);
    window.removeEventListener(`DOMContentLoaded`, this.helpers.handleDomContentLoaded);
  }

  private trackShortUrl(event, params = {}) {
    mixpanel.track(`[Short URL] ${event}`, params);
  }
}

registerMPElement(`global-action-bar`, GlobalActionBar);
